import { useRef, useState, FormEvent } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

interface ApplicationFormProps {
  submitted: boolean;
  setSubmitted: (value: boolean) => void;
}

const ApplicationForm = ({ submitted, setSubmitted }: ApplicationFormProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Background glow intensity
  const glowIntensity = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [0, 0.12, 0]);

  // Progressive field reveals
  const containerY = useTransform(scrollYProgress, [0, 0.3], [100, 0]);
  const containerOpacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);

  const field1Opacity = useTransform(scrollYProgress, [0.15, 0.25], [0, 1]);
  const field1Y = useTransform(scrollYProgress, [0.15, 0.25], [40, 0]);

  const field2Opacity = useTransform(scrollYProgress, [0.2, 0.3], [0, 1]);
  const field2Y = useTransform(scrollYProgress, [0.2, 0.3], [40, 0]);

  const field3Opacity = useTransform(scrollYProgress, [0.25, 0.35], [0, 1]);
  const field3Y = useTransform(scrollYProgress, [0.25, 0.35], [40, 0]);

  const field4Opacity = useTransform(scrollYProgress, [0.3, 0.4], [0, 1]);
  const field4Y = useTransform(scrollYProgress, [0.3, 0.4], [40, 0]);

  const field5Opacity = useTransform(scrollYProgress, [0.35, 0.45], [0, 1]);
  const field5Y = useTransform(scrollYProgress, [0.35, 0.45], [40, 0]);

  const field6Opacity = useTransform(scrollYProgress, [0.4, 0.5], [0, 1]);
  const field6Y = useTransform(scrollYProgress, [0.4, 0.5], [40, 0]);

  const field7Opacity = useTransform(scrollYProgress, [0.45, 0.55], [0, 1]);
  const field7Y = useTransform(scrollYProgress, [0.45, 0.55], [40, 0]);

  const field8Opacity = useTransform(scrollYProgress, [0.5, 0.6], [0, 1]);
  const field8Y = useTransform(scrollYProgress, [0.5, 0.6], [40, 0]);

  const field9Opacity = useTransform(scrollYProgress, [0.55, 0.65], [0, 1]);
  const field9Y = useTransform(scrollYProgress, [0.55, 0.65], [40, 0]);

  const buttonOpacity = useTransform(scrollYProgress, [0.6, 0.7], [0, 1]);
  const buttonY = useTransform(scrollYProgress, [0.6, 0.7], [40, 0]);
  const buttonScale = useTransform(scrollYProgress, [0.6, 0.7], [0.95, 1]);

  const trustOpacity = useTransform(scrollYProgress, [0.65, 0.75], [0, 1]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6rcpuua739gopmlhjlg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background Glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-blue/10 rounded-full blur-[120px]"></div>
        </div>

        <div className="relative z-10 max-w-[700px] mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="bg-dark-card border border-accent-blue rounded-2xl lg:rounded-3xl p-10 lg:p-16 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
              className="w-16 h-16 lg:w-20 lg:h-20 bg-accent-blue rounded-full flex items-center justify-center mx-auto mb-6 lg:mb-8"
            >
              <svg className="w-8 h-8 lg:w-10 lg:h-10 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-3xl lg:text-4xl font-bold text-white mb-4 lg:mb-6"
            >
              Application Submitted
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg lg:text-xl text-gray-400 leading-relaxed"
            >
              Thank you for applying. We'll review your information and reach out within 48 hours to discuss next steps.
            </motion.p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section ref={containerRef} className="relative min-h-[200vh] py-20 lg:py-32 overflow-hidden">
      {/* Dynamic Background Glow */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: glowIntensity }}
      >
        <div className="absolute top-1/3 left-1/3 w-[700px] h-[700px] bg-accent-blue rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/3 right-1/3 w-[700px] h-[700px] bg-accent-blue rounded-full blur-[120px]"></div>
      </motion.div>

      <div className="relative z-10 max-w-[700px] mx-auto px-6 lg:px-8">
        <motion.form
          id="force-multiplier-application"
          data-readdy-form
          onSubmit={handleSubmit}
          style={{
            y: containerY,
            opacity: containerOpacity
          }}
          className="bg-dark-card border border-dark-border rounded-2xl lg:rounded-3xl p-8 lg:p-12"
        >
          <div className="space-y-6 lg:space-y-8">
            {/* Name */}
            <motion.div style={{ opacity: field1Opacity, y: field1Y }}>
              <label htmlFor="name" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Your Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 min-h-[56px]"
                placeholder="John Smith"
              />
            </motion.div>

            {/* Email */}
            <motion.div style={{ opacity: field2Opacity, y: field2Y }}>
              <label htmlFor="email" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 min-h-[56px]"
                placeholder="john@company.com"
              />
            </motion.div>

            {/* Company */}
            <motion.div style={{ opacity: field3Opacity, y: field3Y }}>
              <label htmlFor="company" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Company Name *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 min-h-[56px]"
                placeholder="Your Company"
              />
            </motion.div>

            {/* Website */}
            <motion.div style={{ opacity: field4Opacity, y: field4Y }}>
              <label htmlFor="website" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Company Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 min-h-[56px]"
                placeholder="https://yourcompany.com"
              />
            </motion.div>

            {/* Revenue Range */}
            <motion.div style={{ opacity: field5Opacity, y: field5Y }}>
              <label htmlFor="revenue" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Annual Revenue Range *
              </label>
              <select
                id="revenue"
                name="revenue"
                required
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 cursor-pointer min-h-[56px] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27white%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_1rem_center] bg-no-repeat"
              >
                <option value="">Select range</option>
                <option value="$1M-$5M">$1M - $5M</option>
                <option value="$5M-$10M">$5M - $10M</option>
                <option value="$10M-$25M">$10M - $25M</option>
                <option value="$25M-$50M">$25M - $50M</option>
                <option value="$50M-$100M">$50M - $100M</option>
                <option value="$100M+">$100M+</option>
              </select>
            </motion.div>

            {/* Company Type */}
            <motion.div style={{ opacity: field6Opacity, y: field6Y }}>
              <label htmlFor="company-type" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Company Type *
              </label>
              <select
                id="company-type"
                name="company-type"
                required
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 cursor-pointer min-h-[56px] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27white%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_1rem_center] bg-no-repeat"
              >
                <option value="">Select type</option>
                <option value="AI Company">AI Company</option>
                <option value="SaaS Company">SaaS Company</option>
                <option value="Venture-backed Startup">Venture-backed Startup</option>
                <option value="Other">Other</option>
              </select>
            </motion.div>

            {/* Constraint Description */}
            <motion.div style={{ opacity: field7Opacity, y: field7Y }}>
              <label htmlFor="constraint" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                What constraint are you facing? *
              </label>
              <textarea
                id="constraint"
                name="constraint"
                required
                rows={5}
                maxLength={500}
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 resize-none"
                placeholder="Describe the bottleneck or structural constraint limiting your growth..."
              />
              <p className="text-xs lg:text-sm text-gray-500 mt-2">Maximum 500 characters</p>
            </motion.div>

            {/* Service Interest */}
            <motion.div style={{ opacity: field8Opacity, y: field8Y }}>
              <label htmlFor="service" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Service Interest *
              </label>
              <select
                id="service"
                name="service"
                required
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 cursor-pointer min-h-[56px] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27white%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_1rem_center] bg-no-repeat"
              >
                <option value="">Select service</option>
                <option value="Force Multiplier Audit">Force Multiplier Audit</option>
                <option value="Multiplication Events">Multiplication Events</option>
                <option value="Founder Advisory">Founder Advisory</option>
                <option value="Not sure yet">Not sure yet</option>
              </select>
            </motion.div>

            {/* Additional Info */}
            <motion.div style={{ opacity: field9Opacity, y: field9Y }}>
              <label htmlFor="additional" className="block text-white font-medium mb-2 lg:mb-3 text-sm lg:text-base">
                Additional Information
              </label>
              <textarea
                id="additional"
                name="additional"
                rows={4}
                maxLength={500}
                className="w-full px-5 py-4 lg:px-6 lg:py-4 bg-dark-bg border border-dark-border rounded-xl text-white text-base focus:border-accent-blue focus:outline-none transition-colors duration-300 resize-none"
                placeholder="Anything else we should know?"
              />
              <p className="text-xs lg:text-sm text-gray-500 mt-2">Maximum 500 characters</p>
            </motion.div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-accent-red/10 border border-accent-red rounded-xl p-4"
              >
                <p className="text-accent-red text-sm lg:text-base">{error}</p>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              style={{
                opacity: buttonOpacity,
                y: buttonY,
                scale: buttonScale
              }}
              className="w-full px-8 py-5 lg:py-5 bg-accent-blue text-white text-base lg:text-lg font-medium rounded-full hover:brightness-110 transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 whitespace-nowrap cursor-pointer min-h-[56px] shadow-lg shadow-accent-blue/20 hover:shadow-xl hover:shadow-accent-blue/30"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </motion.button>

            {/* Trust Indicator */}
            <motion.p
              style={{ opacity: trustOpacity }}
              className="text-center text-sm text-gray-500 -mt-2"
            >
              Typical response time: 48 hours
            </motion.p>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default ApplicationForm;